import { ISlide } from "common/interfaces/models";

/**
 * A shared slide lives in the workspace library as a single Slide document whose
 * libraryItemId points at itself. A deck that uses that shared slide more than once
 * cannot reference the library document twice, because a presentation stores its slide
 * order as slideRefs: { [slideId]: index } — a map, so an id can only appear once.
 *
 * Every occurrence beyond the first is therefore stored as an *instance slide*: a Slide
 * document with its own id whose libraryItemId points at the library slide. It holds no
 * content of its own; the content is resolved from the library slide on read, which keeps
 * the library slide the single source of truth exactly as before.
 */

/** Fields an instance slide inherits from the library slide it points at. */
export const SHARED_SLIDE_INHERITED_FIELDS = [
    // Data state (see SLIDE_DATA_STATE_FIELDS)
    "template_id",
    "layout",
    "states",
    "animations",
    "version",
    "migrationVersion",
    "textStylesVersion",
    "metadata",
    "hasBeenEdited",
    // Other content owned by the library slide
    "assets",
    "audioAsset",
    "advanceDelay",
    "advanceOn",
    "sharedModel",
    "slide_notes",
    "template_version",
    "timeline",
    // Thumbnails are rendered from the library slide's content, so instances reuse both the
    // stored thumbnail metadata and the modifiedAt value that busts the thumbnail cache.
    "thumbnails",
    "modifiedAt",
    "modifiedBy"
    // NOTE: _changeId is deliberately not inherited — it version-stamps a single document, and
    // updateSlide() rejects a write whose _changeId doesn't match the document being written.
] as const;

/**
 * True for the library slide itself, or for any instance pointing at one.
 * Note the library slide's libraryItemId is its own id.
 */
export function isSharedSlide(slide: Pick<ISlide, "id" | "libraryItemId">): boolean {
    return !!slide?.libraryItemId;
}

/**
 * True only for the extra occurrences — a Slide document that borrows its content from a
 * different library slide. False for the library slide itself.
 */
export function isSharedSlideInstance(slide: Pick<ISlide, "id" | "libraryItemId">): boolean {
    return !!slide?.libraryItemId && slide.libraryItemId !== slide.id;
}

/**
 * The id of the document that actually holds the content for this slide. For the library
 * slide and for ordinary slides this is the slide's own id, so callers can use it
 * unconditionally.
 */
export function getSlideContentId(slide: Pick<ISlide, "id" | "libraryItemId">): string {
    return isSharedSlideInstance(slide) ? slide.libraryItemId : slide.id;
}

/**
 * Merges the library slide's content onto an instance slide, keeping the instance's own
 * identity and per-deck state (presentationId, isSkipped, assignment, ...).
 */
export function resolveSharedSlideInstance(instance: ISlide, librarySlide: ISlide): ISlide {
    const resolved = { ...instance } as ISlide;

    for (const field of SHARED_SLIDE_INHERITED_FIELDS) {
        if (librarySlide[field] === undefined) {
            delete resolved[field];
        } else {
            resolved[field] = librarySlide[field];
        }
    }

    // States are required by the schema, so an instance always has a placeholder to fall
    // back on if the library slide somehow has none.
    if (!resolved.states) {
        resolved.states = instance.states ?? [{}];
    }

    return resolved;
}
